import { getOrdinal } from '@frontend/utils'
import { isAfter, parseISO } from 'date-fns'
import { generateColorById } from '../../utils'
import { handleName } from '../../utils/helpers'
import {
  formatMetrics,
  getFormattedValue,
  processMetric,
  sortPlayerOptionsByNumber
} from '../metrics/functions'
import { MetricTypeConfigMap, RawMetricValueTypes } from '../metrics/types'
import { OutcomeTypeConfig } from '../outcomes/types'
import {
  FormattedSession,
  FormattedTeamPersonSessions
} from '../sessions/types'
import {
  DataTypeEventConfig,
  EventTypeBase,
  EventTypeConfig,
  HierarchyLevelEventConfig,
  SessionSubTypeConfig,
  SportsConfig
} from '../sports/config'
import {
  EMPTY_DATA_TYPE_EVENT_CONFIG,
  EMPTY_EVENT_TYPE_BASE,
  EMPTY_EVENT_TYPE_CONFIG,
  EMPTY_HIERARCHY_LEVEL_EVENT_CONFIG,
  EMPTY_SESSION_SUB_TYPE_CONFIG,
  EMPTY_SESSION_TYPE_CONFIG
} from '../sports/constants'
import { UnitSystem, UnitSystemKeys } from '../units/types'
import {
  DetailedEventData,
  EventHierarchyLevels,
  ProcessedEventData,
  RawEventData,
  RawFlightEventData,
  RawPhysicalEventData,
  RawSessionEventData,
  RawTimingData
} from './types'

const debug = false

export const generateDetailedEventData = (
  event: RawEventData,
  formattedSession: FormattedSession,
  unitSystem: UnitSystem
): DetailedEventData => {
  const { teams, players, playersSessions } = formattedSession

  // Get event type configs //

  const processedEvent = processEvent(event, formattedSession, unitSystem.key)

  // Metrics
  const metrics = formatMetrics(
    processedEvent.primaryType.metrics,
    processedEvent.metrics.raw,
    formattedSession.teams,
    formattedSession.players.All,
    unitSystem,
    formattedSession.playersSessions.byId,
    formattedSession.teamSessions,
    processedEvent.outcomes
  )

  // Player and Team //
  const team = {
    selected: processedEvent.team,
    options: teams?.options || []
  }

  const player = {
    selected: processedEvent.player,
    options:
      playersSessions?.byId?.options?.sort(
        sortPlayerOptionsByNumber(playersSessions?.byId)
      ) || []
  }

  // Types //
  const typeOptions = Object.values(processedEvent.dataType.types || {}).map(
    (type) => ({
      name: handleName(type.name),
      value: type.value,
      isSettable: type.isSettable
    })
  )

  const type = {
    selected: processedEvent.type,
    options: typeOptions
  }

  const subTypeOptions = Object.values(processedEvent.type?.subTypes || {}).map(
    (subType) => ({
      name: handleName(subType.name),
      value: subType.value
    })
  )

  const subType = {
    selected: processedEvent.subType,
    options: subTypeOptions
  }

  // Outcomes //
  const outcomeOptions = Object.values(
    processedEvent.primaryType.outcomes || {}
  )
    .filter((outcome) => outcome.isSettable)
    .map((outcome) => ({
      name: handleName(outcome.name),
      value: outcome.value
    }))

  const outcomes = {
    selected: processedEvent.outcomes,
    options: outcomeOptions
  }

  return {
    ...processedEvent,

    team: team,
    player: player,

    type,
    subType,

    metrics,

    outcomes
  }
}

export function validateModifiedFlightEventData(
  modifiedEvent: RawFlightEventData,
  formattedSession: FormattedSession,
  unitSystem: UnitSystem
): RawFlightEventData {
  const copiedFlightData: RawFlightEventData = {
    ...modifiedEvent,
    data: {
      ...modifiedEvent.data
    }
  }

  const updatedDetailedEventData = generateDetailedEventData(
    modifiedEvent,
    formattedSession,
    unitSystem
  )

  const { subType: updatedSubType, outcomes: updatedOutcomes } =
    updatedDetailedEventData

  const subTypeCompatible = updatedSubType.options.some(
    (option) => option.value === copiedFlightData.data.subType
  )
  if (!subTypeCompatible) copiedFlightData.data.subType = 'Unclassified'

  // Remove any outcomes, where the outcomes are no longer applicable
  copiedFlightData.outcomes =
    copiedFlightData.outcomes?.filter((outcome) =>
      updatedOutcomes.options.some((option) => outcome === option.value)
    ) ?? null

  return copiedFlightData
}

export function getSessionEventConfig(
  sportsConfig: SportsConfig | null,
  sportType: string,
  sessionType: string,
  sessionSubType: string
) {
  const sportEventsConfig = sportsConfig?.sportTypes?.[sportType]?.events

  if (!sportEventsConfig) {
    if (debug)
      console.warn(
        `Sport events config not found for selected sport type: ${sportType}`
      )
    return {
      sessionTypeConfig: EMPTY_SESSION_TYPE_CONFIG,
      sessionSubTypeConfig: EMPTY_SESSION_SUB_TYPE_CONFIG
    }
  }

  const sessionTypeConfig =
    sportEventsConfig.Session.sessionTypes?.[sessionType]

  if (!sessionTypeConfig) {
    if (debug)
      console.warn(`Type events config not found for type: ${sessionType}`)
    return {
      sessionTypeConfig: EMPTY_SESSION_TYPE_CONFIG,
      sessionSubTypeConfig: EMPTY_SESSION_SUB_TYPE_CONFIG
    }
  }

  const sessionSubTypeConfig =
    sessionTypeConfig.sessionSubTypes?.[sessionSubType]

  if (!sessionSubTypeConfig) {
    if (debug)
      console.warn(
        `Session sub type events config not found for sub type: ${sessionSubType}`
      )
    return {
      sessionTypeConfig: sessionTypeConfig,
      sessionSubTypeConfig: EMPTY_SESSION_SUB_TYPE_CONFIG
    }
  }

  return {
    sessionTypeConfig,
    sessionSubTypeConfig
  }
}

export function getTypeConfigs(
  sessionSubTypeEventsConfig: SessionSubTypeConfig,
  hierarchyLevel: string,
  dataType: string,
  type: string,
  subType: string
): {
  hierarchyLevelConfig: HierarchyLevelEventConfig
  dataTypeConfig: DataTypeEventConfig
  eventTypeConfig: EventTypeConfig
  eventSubTypeConfig: EventTypeConfig['subTypes'][string]
  primaryType:
    | EventTypeConfig
    | EventTypeBase
    | HierarchyLevelEventConfig
    | DataTypeEventConfig
} {
  const hierarchyLevelConfig =
    sessionSubTypeEventsConfig.hierarchyLevels?.[hierarchyLevel]

  if (!hierarchyLevelConfig) {
    if (debug)
      console.warn(
        `Hierarchy level config not found for hierarchy level: ${hierarchyLevel}`
      )
    return {
      hierarchyLevelConfig: EMPTY_HIERARCHY_LEVEL_EVENT_CONFIG,
      dataTypeConfig: EMPTY_DATA_TYPE_EVENT_CONFIG,
      eventTypeConfig: EMPTY_EVENT_TYPE_CONFIG,
      eventSubTypeConfig: EMPTY_EVENT_TYPE_BASE,
      primaryType: EMPTY_EVENT_TYPE_BASE
    }
  }

  const dataTypeConfig = hierarchyLevelConfig?.dataTypes[dataType]

  if (!dataTypeConfig) {
    if (debug)
      console.warn(`Data type config not found for data type: ${dataType}`)
    return {
      hierarchyLevelConfig,
      dataTypeConfig: EMPTY_DATA_TYPE_EVENT_CONFIG,
      eventTypeConfig: EMPTY_EVENT_TYPE_CONFIG,
      eventSubTypeConfig: EMPTY_EVENT_TYPE_BASE,
      primaryType: hierarchyLevelConfig
    }
  }

  const eventTypeConfig = dataTypeConfig.types[type]

  if (!eventTypeConfig) {
    if (debug) console.warn(`Data type config not found for type: ${type}`)
    return {
      hierarchyLevelConfig,
      dataTypeConfig,
      eventTypeConfig: EMPTY_EVENT_TYPE_CONFIG,
      eventSubTypeConfig: EMPTY_EVENT_TYPE_BASE,
      primaryType: dataTypeConfig
    }
  }

  if (!eventTypeConfig.subTypes) {
    return {
      hierarchyLevelConfig,
      dataTypeConfig,
      eventTypeConfig,
      eventSubTypeConfig: EMPTY_EVENT_TYPE_BASE,
      primaryType: eventTypeConfig
    }
  }

  const eventSubTypeConfig = eventTypeConfig.subTypes[subType]

  if (!eventSubTypeConfig) {
    if (debug)
      console.warn(
        `Event sub type config not found for sub type: ${subType} in type: ${type}`
      )
    return {
      hierarchyLevelConfig,
      dataTypeConfig,
      eventTypeConfig,
      eventSubTypeConfig: EMPTY_EVENT_TYPE_BASE,
      primaryType: eventTypeConfig
    }
  }

  return {
    hierarchyLevelConfig,
    dataTypeConfig,
    eventTypeConfig,
    eventSubTypeConfig,
    primaryType: eventSubTypeConfig
  }
}

export function getDataTypeEventConfig(
  sessionSubTypeEventsConfig: SessionSubTypeConfig,
  hierarchyLevel: string,
  dataType: string
) {
  if (!sessionSubTypeEventsConfig) {
    if (debug) console.warn('Session sub type events config is not provided')
    return EMPTY_DATA_TYPE_EVENT_CONFIG
  }

  const hierarchyLevelEventTypeConfig =
    sessionSubTypeEventsConfig.hierarchyLevels?.[hierarchyLevel]

  if (!hierarchyLevelEventTypeConfig) {
    if (debug)
      console.warn(
        `Hierarchy level event type config not found for hierarchy level: ${hierarchyLevel}`
      )
    return EMPTY_DATA_TYPE_EVENT_CONFIG
  }

  const dataTypeEventConfig = hierarchyLevelEventTypeConfig.dataTypes[dataType]

  if (!dataTypeEventConfig) {
    if (debug)
      console.warn(
        `Flight event type config not found for session sub type: ${dataType}`
      )
    return EMPTY_DATA_TYPE_EVENT_CONFIG
  }

  return dataTypeEventConfig
}

export function getEventTeamPersonSessionId(event: RawEventData) {
  if (event.hierarchy_level !== 'SportEvent') return null

  // Get Player ID from FreeJson data types //
  if (event.dataType === 'FreeJson') {
    return event.properties?.playerId || null
  }

  if (event.dataType !== 'Flight') return null

  const launchEvent = event.children?.find((child) => {
    if (child.dataType !== 'PhysicalEvent') return false
    return child.data?.type === 'Launch'
  })

  if (!launchEvent) return null

  if (launchEvent.dataType !== 'PhysicalEvent') return null

  return launchEvent?.data?.teamPersonSessionId || null
}

export function getEventTeamId(event: RawEventData) {
  // Get Team ID from FreeJson data types //
  if (event.dataType === 'FreeJson') {
    return event.properties?.teamId || null
  }

  if (event.data && 'teamId' in event.data) {
    return event.data.teamId
  }

  return null
}

export function resolveEventTeamId(
  event: RawEventData,
  playersSessions: FormattedTeamPersonSessions
): string | null {
  const teamId = getEventTeamId(event)
  if (teamId) return teamId

  const playerId = getEventTeamPersonSessionId(event)
  if (!playerId) return null

  const player = playersSessions.byId.map[playerId]
  return player?.teamId ?? null
}

export function processEventMetrics(
  event: RawEventData,
  metricTypeConfigMap: MetricTypeConfigMap,
  formattedSession: FormattedSession,
  unitSystemValue: UnitSystemKeys
) {
  const rawMetrics: Record<string, any> = {}
  const processedMetrics: Record<string, any> = {}
  const formattedMetrics: Record<string, any> = {}

  if (!event.data) {
    if (debug)
      console.warn(
        `Event data does not have metrics: ${event.dataType} - ${event.id}`
      )
    return { rawMetrics, processedMetrics, formattedMetrics }
  }

  // event.data is a discriminated union of concrete event shapes; the metric
  // keys read below are driven dynamically by metricTypeConfigMap and live on
  // no single shape. Bridge to an indexable record so those lookups type.
  // TODO: revisit cast.
  const eventData = event.data as Record<
    string,
    RawMetricValueTypes | null | undefined
  >

  // event.properties is likewise a discriminated union of concrete shapes;
  // a properties-sourced metric key is dynamic and lives on no single shape.
  // Bridge to an indexable record so the lookup types, mirroring eventData.
  // TODO: revisit cast.
  const eventProperties = event.properties as
    Record<string, RawMetricValueTypes | null | undefined> | undefined

  // Metrics //
  for (const key in metricTypeConfigMap) {
    const metricTypeConfig = metricTypeConfigMap[key]

    // TODO: Figure something out for cutom metrics //
    // Do custom raw values //
    // if (metricType.props.type === 'number' && metricType.props.getValue) {
    //   rawMetrics[key] = props.getValue(rawMetrics)
    // }

    const value =
      metricTypeConfig.source === 'properties'
        ? eventProperties?.[key]
        : eventData[key]

    if (
      metricTypeConfig.source !== 'properties' &&
      (value === undefined || value === null)
    ) {
      // If the metric is not present in the raw data, skip processing.
      // Properties-sourced metrics never skip: an absent value still writes
      // an explicit null below.
      continue
    }

    const processedMetricValue = processMetric(
      metricTypeConfig,
      value,
      rawMetrics,
      formattedSession.teams,
      formattedSession.players.byTeamPersonSessionId,
      unitSystemValue
    )

    const formattedMetricValue = getFormattedValue(
      metricTypeConfig,
      processedMetricValue
    )

    // Properties-sourced metrics always write, with an explicit `null` when
    // absent — a skip would leave the key undefined, which a select renders
    // as the literal string "undefined".
    rawMetrics[key] = value || null
    processedMetrics[key] = processedMetricValue
    formattedMetrics[key] = formattedMetricValue
  }

  return {
    rawMetrics,
    processedMetrics,
    formattedMetrics
  }
}

export function processEvent(
  event: RawEventData,
  formattedSession: FormattedSession,
  unitSystemValue: UnitSystemKeys
): ProcessedEventData {
  const { sessionEventConfig } = formattedSession

  // Get event type configs //
  const dataPropTocheck =
    event.dataType !== 'FreeJson' ? event.data : event.properties
  const extractedType =
    dataPropTocheck && 'type' in dataPropTocheck ? dataPropTocheck.type : null
  const extractedSubType =
    dataPropTocheck && 'subType' in dataPropTocheck
      ? dataPropTocheck.subType
      : null

  const {
    hierarchyLevelConfig,
    dataTypeConfig,
    eventTypeConfig,
    eventSubTypeConfig,
    primaryType
  } = getTypeConfigs(
    sessionEventConfig,
    event.hierarchy_level,
    event.dataType,
    extractedType ?? '',
    extractedSubType ?? ''
  )

  // Time //
  const sessionTime =
    new Date(event.startTime).getTime() - formattedSession.startTime.unixMil

  // Outcomes //
  const outcomeTypes = primaryType.outcomes

  const outcomes =
    event.outcomes
      ?.map((outcome) => {
        const outcomeType = outcomeTypes[outcome]
        if (!outcomeType) {
          if (debug)
            console.warn(
              `Outcome type not found for outcome: ${outcome} in event: ${event.id}`
            )
          return null
        }
        return outcomeType
      })
      .filter((outcome): outcome is OutcomeTypeConfig => outcome !== null) || []

  // Team and Player //
  const playerId = getEventTeamPersonSessionId(event)
  const player = formattedSession.playersSessions.byId.map[playerId ?? '']
  const teamId = resolveEventTeamId(event, formattedSession.playersSessions)
  const team = formattedSession.teams.map[teamId ?? '']

  // Values //
  const { rawMetrics, processedMetrics, formattedMetrics } =
    processEventMetrics(
      event,
      primaryType.metrics,
      formattedSession,
      unitSystemValue
    )

  // Team and Player //

  const processedEvent: ProcessedEventData = {
    ...event,
    id: event.id,
    rawData: event,
    sessionId: formattedSession.id,

    team,
    player,

    hierarchyLevel: hierarchyLevelConfig,
    dataType: dataTypeConfig,
    type: eventTypeConfig,
    subType: eventSubTypeConfig,
    primaryType,

    // TODO: Operator Notes! //
    // operatorNotes: flightEvent.operatorNotes || {
    //   id: null,
    //   notes: null,
    //   highlight: null,
    //   matchTime: null
    // },

    metrics: {
      raw: rawMetrics,
      processed: processedMetrics,
      formatted: formattedMetrics
    },

    sessionTime,
    outcomes
  }

  return processedEvent
}

// MQTT //

export const subscribeToEventTopics = (
  client:
    | {
        subscribe: (
          topic: string,
          cb: (data: RawEventData, topic: string) => void
        ) => void
      }
    | null
    | undefined,
  locationName: string,
  handleMessage: (data: RawEventData) => void
) => {
  // Subscribe to mqtt topic
  const topicName = `v1/${locationName}/events/all/SI`

  if (client) {
    client.subscribe(topicName, (data, topic) => {
      handleMessage(data)
    })

    return topicName
  }
}

export function findSessionFromEventId(
  eventId: string,
  eventEntities: Record<RawEventData['id'], RawEventData>
): RawSessionEventData | null {
  let currentEvent: RawEventData | undefined = eventEntities[eventId]

  while (currentEvent) {
    if (currentEvent.hierarchy_level === 'Session') {
      return currentEvent
    }
    currentEvent = eventEntities[currentEvent.parentId]
  }

  return null // No session found in the hierarchy
}

// Transform Event Data //
// This is required to add receiverId and player to the event data for flight events //
export const transformEventData = (data: RawEventData): RawEventData => {
  // Handle session events
  if (data.dataType === 'Session') {
    if (data.data && !data.data.players) {
      // Ensure players array exists - return new object instead of mutating
      return {
        ...data,
        data: {
          ...data.data,
          players: []
        }
      }
    }
    return data
  }

  // Handle Drill events
  if (data.dataType === 'Drill') {
    return {
      ...data,
      color: generateColorById(`${data.id}-drill`)
    }
  }

  // Handle Flight events (existing logic)
  if (data.dataType === 'Flight') {
    const playerId = getEventTeamPersonSessionId(data)

    const receiverEvent = data.children?.find((child) => {
      if (child.dataType !== 'PhysicalEvent') return false
      return child.data?.type === 'End'
    }) as RawPhysicalEventData | undefined

    const receiverId = receiverEvent?.data?.teamPersonSessionId || null

    const targetPlayerId = data?.properties?.targetPlayerId || null

    return {
      ...data,
      data: {
        ...data.data,
        playerId,
        receiverId,
        targetPlayerId
      }
    }
  }

  // Return unchanged for other event types
  return data
}

export const buildChildrenMap = (
  events: RawEventData[],
  parentIds?: string[]
): Map<string, RawEventData[]> => {
  const childrenByParentId = new Map<string, RawEventData[]>()

  if (parentIds) {
    for (const id of parentIds) childrenByParentId.set(id, [])
  }

  for (const event of events) {
    if (event.parentId) {
      const siblings = childrenByParentId.get(event.parentId)
      if (siblings) {
        siblings.push(event)
      } else if (!parentIds) {
        childrenByParentId.set(event.parentId, [event])
      }
    }
  }
  return childrenByParentId
}

/**
 * Counts the SportEvent-level descendants (flights / game events) of a parent
 * event, using a children-by-parentId map (see `buildChildrenMap`).
 *
 * Recursion stops at the SportEvent level so a flight's own child events
 * (e.g. physical events) are not counted. Used when undoing a timing: its
 * descendant sport events get reassigned to the session while the
 * intermediate hierarchy (possessions/plays) is deleted.
 */
export const countDescendantSportEvents = (
  parentId: string,
  childrenByParentId: Map<string, RawEventData[]>
): number => {
  const children = childrenByParentId.get(parentId) ?? []
  return children.reduce((count, child) => {
    if (child.hierarchy_level === 'SportEvent') return count + 1
    return count + countDescendantSportEvents(child.id, childrenByParentId)
  }, 0)
}

/**
 * Counts the direct children of a parent (e.g. the session) whose startTime is
 * after the given cutoff, using a children-by-parentId map.
 *
 * Used when undoing a timing's end: once a timing ends there is no open period,
 * so events coded afterwards attach directly to the session as flights. They
 * are reassigned back into the re-opened timing, so a flat count of session
 * children after the timing's endTime is the number of flights affected — no
 * descent is needed (no possessions/plays exist outside a timing).
 */
export const countSessionEventsAfter = (
  sessionId: string,
  afterTime: string,
  childrenByParentId: Map<string, RawEventData[]>
): number => {
  const sessionChildren = childrenByParentId.get(sessionId) ?? []
  return sessionChildren.filter((child) =>
    isAfter(parseISO(child.startTime), parseISO(afterTime))
  ).length
}

export const batchTransformEventData = (
  data: RawEventData[]
): RawEventData[] => {
  // Build a lookup of children by parentId for flat event lists.
  // When events are fetched with flatten: true, children arrays are empty
  // and child events appear as separate entries in the flat list.
  const childrenByParentId = buildChildrenMap(data)

  return data.map((event) => {
    // For Flight events with empty children, inject children from the flat lookup
    if (
      event.dataType === 'Flight' &&
      (!event.children || event.children.length === 0)
    ) {
      const flatChildren = childrenByParentId.get(event.id)
      if (flatChildren && flatChildren.length > 0) {
        return transformEventData({
          ...event,
          children: flatChildren
        })
      }
    }
    return transformEventData(event)
  })
}

export const isEventDescendantOf = (
  eventEntities: Record<string, RawEventData>,
  childId: string,
  targetParentId: string,
  initialParentId?: string
): boolean => {
  let currentParentId = initialParentId || eventEntities[childId]?.parentId

  while (currentParentId) {
    if (currentParentId === targetParentId) {
      return true
    }

    currentParentId = eventEntities[currentParentId]?.parentId
  }

  return false
}

// Timing //

// Private: the only caller is generatePhaseLabel below. Consumers must use
// generatePhaseLabel so the overtime-ordinal logic is never bypassed.
const generatePeriodLabel = (periodNumber: number, periodName: string) => {
  return `${getOrdinal(periodNumber)} ${periodName}`
}

// Backend PeriodNumber is an int8 column — hard ceiling of 127 periods. //
// No real match approaches this; the guard prevents silent overflow. //
export const MAX_PERIOD_NUMBER = 127

export type PeriodPhaseType = RawTimingData['type'] // 'Period' | 'Overtime'

export type NextPhase = {
  type: PeriodPhaseType
  periodNumber: number
}

export type TimingFeatures = {
  periodCount: number
  // Advisory soft cap on overtime periods: a number is the expected count;
  // null/absent means unlimited. Never gates Start — only the warning copy.
  standardOvertimeCount?: number | null
}

// True once a period number is past regulation. //
export const isOvertime = (
  periodNumber: number,
  periodCount: number
): boolean => periodNumber > periodCount

// 1-based overtime ordinal (first overtime period = 1). //
// Only meaningful when isOvertime(periodNumber, periodCount) is true. //
export const getOvertimeOrdinal = (
  periodNumber: number,
  periodCount: number
): number => periodNumber - periodCount

// Regulation is complete once the period number has reached the period count. //
export const isRegulationComplete = (
  periodNumber: number,
  periodCount: number
): boolean => periodNumber >= periodCount

// The single source of "what period comes next". Period during regulation, //
// Overtime (unbounded) once regulation is complete. Returns null only at the //
// int8 ceiling guard. The standard overtime count does NOT change the output — //
// it only feeds the advisory warning in the controls. //
export const getNextPhase = (
  current: Pick<RawTimingData, 'periodNumber'> | null,
  features: TimingFeatures
): NextPhase | null => {
  const nextPeriodNumber = current ? current.periodNumber + 1 : 1

  if (nextPeriodNumber > MAX_PERIOD_NUMBER) return null

  return {
    type: isOvertime(nextPeriodNumber, features.periodCount)
      ? 'Overtime'
      : 'Period',
    periodNumber: nextPeriodNumber
  }
}

// Label a period by its in-type ordinal: regulation periods count from 1
// ("1st Half", "2nd Quarter"); overtime periods restart from 1 and read the
// same way ("1st Extra Time", "2nd Overtime"). phaseName is the sport's type name.
export const generatePhaseLabel = (
  periodNumber: number,
  periodCount: number,
  phaseName: string
): string => {
  const ordinal = isOvertime(periodNumber, periodCount)
    ? getOvertimeOrdinal(periodNumber, periodCount)
    : periodNumber
  return generatePeriodLabel(ordinal, phaseName)
}

// Hierarchy Levels //

export const listIncludesHierarchyLevel = (
  list: any[],
  hierarchyLevel: EventHierarchyLevels
) => {
  return list.some((element) => element === hierarchyLevel)
}

export function isCodedEvent(event: RawEventData): boolean {
  return event.properties?.codedEvent?.source === 'coded-event'
}
